import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import OutputFormatPicker from '../shared/OutputFormatPicker';
import {
    IconCircleOff,
    IconColorPicker,
    IconDownload,
    IconPalette,
    IconPhoto,
    IconRefresh,
    IconPlus,
    IconX,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { useTranslation } from '../../../../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../../../../utils/bnFormat';
import { recordToolUsage } from '../../../../../services/toolsService';
import { zipImageFiles } from '../../pdf/pdf-to-image/pdfToImageEngine';
import useImageFileEdits from '../../components/image/useImageFileEdits';
import useImagePaste from '../../components/image/useImagePaste';
import ToolPageLayout from '../../components/common/ToolPageLayout';
import ToolPreviewPanel from '../../components/common/ToolPreviewPanel';
import Hint from '../../../../../components/common/Hint';
import ImageGridPreview from '../shared/ImageGridPreview';
import PhotoBoothInbox from '../../../../../components/common/PhotoBoothInbox';
import ImageCompareSliderModal from '../../../../../components/common/ImageCompareSliderModal';
import { attachDisplayUrl, toDisplayableBlob } from '../shared/heicDisplay';
import { resolveOutputFormat } from '../shared/imageOutputFormats';
import {
    composeWithBackground,
    createBackgroundRemover,
    isSupportedRemoveBgFile,
    outputFileName,
    toDrawableBlob,
} from './removeBackgroundEngine';
import {
    BACKGROUND_COLOR_PATTERNS,
    BACKGROUND_COLOR_SWATCHES,
    BACKGROUND_PHOTO_PRESETS,
    patternCss,
} from './removeBackgroundPresets';
import {
    deriveToolStatus,
    downloadBlob,
    getToolStatusText,
} from '../../toolPageUtils';

const MAX_FILE_BYTES = 50 * 1024 * 1024;
// What the ছবি mode starts on, so switching to it shows a background at once.
const DEFAULT_PHOTO_PRESET_ID = 'tropical-beach';
const ACCEPT = 'image/jpeg,image/png,image/webp,image/avif,image/heic,image/heif,.jpg,.jpeg,.png,.webp,.avif,.heic,.heif,.hif';

const BG_MODE_OPTIONS = [
    { id: 'transparent', labelKey: 'removeBackground.bgModes.transparent' },
    { id: 'color', labelKey: 'removeBackground.bgModes.color' },
    { id: 'image', labelKey: 'removeBackground.bgModes.image' },
];

// স্বচ্ছ uses the AI editor's "none" glyph, so "no background" reads the same everywhere.
const BG_MODE_ICONS = { transparent: IconCircleOff, color: IconPalette, image: IconPhoto };

function segBtnClass(active) {
    return `flex flex-1 min-h-[54px] flex-col items-center justify-center gap-1 rounded-lg border-none px-1 py-1.5 text-[11px] leading-tight transition-all disabled:cursor-not-allowed disabled:opacity-45 ${
        active
            ? 'bg-white font-semibold text-amber-600 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:bg-zinc-900 dark:text-amber-400'
            : 'bg-transparent font-medium text-[#6B635C] dark:text-zinc-400'
    }`;
}

const fileKey = (file) => `${file.name}-${file.size}`;

// The result arrays are indexed by file, so an edit to one card has to move them
// with it — otherwise a delete would shift every cut-out onto the wrong picture.
// A plain `.filter` would not do: these arrays are sparse while a batch is still
// running, and filter drops the holes.
function withoutIndex(arr, index) {
    const next = [];
    for (let i = 0; i < arr.length; i += 1) {
        if (i !== index) next.push(arr[i]);
    }
    return next;
}

function withHoleAfter(arr, index) {
    const next = arr.slice();
    next.splice(index + 1, 0, undefined);
    return next;
}

function clearedAt(arr, index) {
    if (index >= arr.length) return arr;
    const next = arr.slice();
    next[index] = undefined;
    return next;
}

export default function RemoveBackgroundImage() {
    const { t } = useTranslation();
    const fileInputRef = useRef(null);
    const bgImageInputRef = useRef(null);

    const [files, setFiles] = useState([]);
    // The queue runs while the grid stays editable, so a finished result is
    // written by looking the file up again rather than by the index it had when
    // the run started — deleting a card mid-run must not shift someone else's
    // cut-out onto the wrong picture.
    const filesRef = useRef(files);
    filesRef.current = files;
    // Left-column tabs: 'settings' = the tool controls, 'booth' = the photo-booth
    // queue (tab exists only while the booth is ON). Booth photos already in the
    // batch stay checked — pruned when their File leaves `files`.
    const [sideTab, setSideTab] = useState('settings');
    const [boothOn, setBoothOn] = useState(false);
    const boothFileIds = useRef(new Map()); // File -> booth upload id
    const addedBoothIds = files.filter((f) => boothFileIds.current.has(f)).map((f) => boothFileIds.current.get(f));
    // Raw cut-outs from the model, indexed by file. The background is applied on
    // top of these, so changing it never re-runs the model.
    const [cutouts, setCutouts] = useState([]);
    const [composed, setComposed] = useState([]);
    const [bgMode, setBgMode] = useState('transparent');
    const [bgColor, setBgColor] = useState('#ffffff');
    // A gradient / pattern preset picked in the colour tab. While one is set it
    // is what gets painted; picking a solid or the custom colour clears it.
    const [bgPatternId, setBgPatternId] = useState(null);
    const bgPattern = useMemo(
        () => BACKGROUND_COLOR_PATTERNS.find((preset) => preset.id === bgPatternId) || null,
        [bgPatternId],
    );
    const [format, setFormat] = useState('png');
    const [bgImageFile, setBgImageFile] = useState(null);
    // A hosted photo preset for the ছবি mode. An uploaded background replaces it,
    // and picking a preset drops the upload — only one of the two is ever set.
    const [bgPhotoId, setBgPhotoId] = useState(null);
    const bgPhoto = useMemo(
        () => BACKGROUND_PHOTO_PRESETS.find((preset) => preset.id === bgPhotoId) || null,
        [bgPhotoId],
    );
    // Object URL for the chosen background, so the panel can show a thumbnail of
    // exactly what will sit behind the cut-out.
    const [bgImageUrl, setBgImageUrl] = useState(null);
    const [isDragging, setIsDragging] = useState(false);
    const [processing, setProcessing] = useState(false);
    const [progress, setProgress] = useState(null);
    const [previewIndex, setPreviewIndex] = useState(0);
    // Indexes whose last run failed. One bad picture in a batch must not throw
    // away the ones that worked, so failures are kept per file instead of as a
    // single page-wide error.
    const [failedIndexes, setFailedIndexes] = useState([]);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (!bgImageFile) {
            setBgImageUrl(null);
            return undefined;
        }
        // HEIC is decoded first — a raw .heic thumbnail paints nothing outside Safari.
        return attachDisplayUrl(bgImageFile, setBgImageUrl);
    }, [bgImageFile]);

    const invalidateResults = () => {
        setCutouts([]);
        setComposed([]);
        setError(null);
        setFailedIndexes([]);
        setProgress(null);
        setPreviewIndex(0);
    };

    const addFiles = useCallback((incoming) => {
        const list = Array.from(incoming || []);
        const images = list.filter(isSupportedRemoveBgFile);
        if (!images.length) {
            const msg = t('removeBackground.errors.invalidType');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            return;
        }

        let tooLarge = false;
        setFiles((prev) => {
            const next = [...prev];
            for (const file of images) {
                if (file.size > MAX_FILE_BYTES) {
                    tooLarge = true;
                    continue;
                }
                if (next.some((f) => fileKey(f) === fileKey(file))) continue;
                next.push(file);
            }
            return next;
        });

        if (tooLarge) {
            const msg = t('removeBackground.errors.tooLarge');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } else {
            setError(null);
        }
    }, [t]);

    // Ctrl/Cmd+V anywhere on the page drops a copied picture straight in.
    useImagePaste(addFiles);

    const handleDrop = (e) => {
        e.preventDefault();
        setIsDragging(false);
        addFiles(e.dataTransfer.files);
    };

    // Editing one card keeps every other result: only that picture's cut-out is
    // dropped, so a delete or a crop late in a batch does not force a full re-run.
    const removeFile = (index) => {
        setFiles((prev) => prev.filter((_, i) => i !== index));
        setCutouts((prev) => withoutIndex(prev, index));
        setComposed((prev) => withoutIndex(prev, index));
        setFailedIndexes((prev) => prev.filter((i) => i !== index).map((i) => (i > index ? i - 1 : i)));
        setPreviewIndex(0);
        setError(null);
    };

    // Crop / rotate / duplicate / delete, shared with every other image tool.
    // The edits rewrite the picked file, so the pipeline below is untouched.
    const { actions: fileActions, cropDialog } = useImageFileEdits({
        files,
        onReplace: (index, file) => {
            setFiles((prev) => prev.map((item, i) => (i === index ? file : item)));
            // The picture changed, so its old cut-out is stale — that one card goes
            // back into the queue and the run button lights up again.
            setCutouts((prev) => clearedAt(prev, index));
            setComposed((prev) => clearedAt(prev, index));
            setFailedIndexes((prev) => prev.filter((i) => i !== index));
            setError(null);
        },
        onInsert: (index, file) => {
            setFiles((prev) => {
                const next = [...prev];
                next.splice(index + 1, 0, file);
                return next;
            });
            setCutouts((prev) => withHoleAfter(prev, index));
            setComposed((prev) => withHoleAfter(prev, index));
            setFailedIndexes((prev) => prev.map((i) => (i > index ? i + 1 : i)));
            setError(null);
        },
        onRemove: removeFile,
    });

    const handleReset = () => {
        setFiles([]);
        setBgMode('transparent');
        setBgColor('#ffffff');
        setBgPatternId(null);
        setBgPhotoId(null);
        setBgImageFile(null);
        invalidateResults();
    };

    // Files with no cut-out yet — adding pictures to a finished batch queues only
    // the new ones, so nothing already processed goes through the model twice.
    const pendingIndexes = useMemo(
        () => files.reduce((acc, _file, i) => (cutouts[i] ? acc : [...acc, i]), []),
        [files, cutouts],
    );
    // A picture that failed counts as resolved for the "batch finished" gate —
    // otherwise one bad photo would hide the download button from every good one.
    const unresolvedIndexes = useMemo(
        () => pendingIndexes.filter((i) => !failedIndexes.includes(i)),
        [pendingIndexes, failedIndexes],
    );
    const allDone = files.length > 0 && unresolvedIndexes.length === 0 && cutouts.some(Boolean);
    const canRun = unresolvedIndexes.length > 0;

    const handleRemoveBackground = async () => {
        if (!canRun || processing) return;

        recordToolUsage('image/remove-background', { category: 'image' });

        setProcessing(true);
        setError(null);
        setFailedIndexes([]);
        const queue = unresolvedIndexes;
        // One worker for the whole batch: the model is fetched once, so only the
        // first picture pays for it.
        const remover = createBackgroundRemover();
        const failed = [];

        try {
            await new Promise((r) => requestAnimationFrame(r));

            for (let k = 0; k < queue.length; k += 1) {
                const i = queue[k];
                const file = files[i];
                setProgress({ fileIndex: k + 1, fileTotal: queue.length, pct: 0 });

                // Each picture stands on its own: a photo the model chokes on marks
                // that one card and the queue carries on with the rest.
                let url = null;
                try {
                    // HEIC is decoded to JPEG and AVIF re-encoded as PNG first — the
                    // model only takes JPG/PNG/WebP.
                    url = URL.createObjectURL(await toDrawableBlob(file));
                    const blob = await remover.run(url, (pct) => {
                        setProgress({ fileIndex: k + 1, fileTotal: queue.length, pct });
                    });
                    // Published immediately, so this card stops spinning and its
                    // tools light up while the rest of the batch is still running.
                    const at = filesRef.current.indexOf(file);
                    if (at >= 0) {
                        setCutouts((prev) => {
                            const next = [...prev];
                            next[at] = blob;
                            return next;
                        });
                    }
                } catch {
                    const at = filesRef.current.indexOf(file);
                    if (at >= 0) {
                        failed.push(at);
                        setFailedIndexes((prev) => [...prev, at]);
                    }
                } finally {
                    if (url) URL.revokeObjectURL(url);
                }
            }

            if (!failed.length) {
                notifications.show({ title: t('common.success'), message: t('removeBackground.done'), color: 'green' });
            } else if (failed.length === queue.length) {
                // Nothing came through — that is the only case worth taking over
                // the whole preview panel.
                const msg = t('removeBackground.errors.failed');
                setError(msg);
                notifications.show({ title: t('common.error'), message: msg, color: 'red' });
            } else {
                notifications.show({
                    title: t('common.warning'),
                    message: t('removeBackground.errors.someFailed', { count: toBnDigits(failed.length) }),
                    color: 'orange',
                });
            }
        } catch {
            const msg = t('removeBackground.errors.failed');
            setError(msg);
            notifications.show({ title: t('common.error'), message: msg, color: 'red' });
        } finally {
            remover.dispose();
            setProcessing(false);
            setProgress(null);
        }
    };

    // The background is painted onto the cut-outs here, not at download time, so
    // the grid shows exactly what the file will contain. Transparent mode needs
    // no work at all — the cut-out is already the answer.
    useEffect(() => {
        if (!cutouts.length) {
            setComposed([]);
            return undefined;
        }
        // Only a PNG cut-out can skip this: any other format has to be re-encoded.
        if (format === 'png' && (bgMode === 'transparent' || (bgMode === 'image' && !bgImageFile && !bgPhoto))) {
            setComposed(cutouts.slice());
            return undefined;
        }

        let cancelled = false;
        (async () => {
            const out = [];
            let applyFailed = false;
            for (let i = 0; i < cutouts.length; i += 1) {
                const cutout = cutouts[i];
                if (!cutout) { out[i] = undefined; continue; }
                try {
                    // "আসলটাই" means whatever each picture arrived as, so the format
                    // is resolved per file rather than once for the batch.
                    const fileFormat = resolveOutputFormat(format, files[i]);
                    // eslint-disable-next-line no-await-in-loop
                    out[i] = await composeWithBackground(cutout, {
                        bgMode, bgColor, bgPattern, bgImageFile, bgPhotoUrl: bgPhoto?.url, format: fileFormat,
                    });
                } catch {
                    // Keep the cut-out, but say so — a background that quietly never
                    // appears (a preset that failed to load) looks like a bug.
                    out[i] = cutout;
                    applyFailed = true;
                }
                if (cancelled) return;
            }
            if (cancelled) return;
            setComposed(out);
            if (applyFailed) {
                notifications.show({
                    title: t('common.error'),
                    message: t('removeBackground.errors.bgApplyFailed'),
                    color: 'red',
                });
            }
        })();

        return () => { cancelled = true; };
    }, [cutouts, files, bgMode, bgColor, bgPattern, bgImageFile, bgPhoto, format]);

    // What the grid, the lightbox and the download all read from.
    const results = useMemo(
        () => files.map((file, i) => {
            const blob = composed[i] ?? cutouts[i];
            if (!blob) return undefined;
            return {
                name: outputFileName(file.name, bgMode, blob),
                originalSize: file.size,
                blob,
            };
        }),
        [files, cutouts, composed, bgMode],
    );

    const doneResults = useMemo(() => results.filter(Boolean), [results]);

    const handleDownload = async () => {
        if (!doneResults.length) return;

        if (doneResults.length === 1) {
            downloadBlob(doneResults[0].blob, doneResults[0].name);
            return;
        }

        const zip = await zipImageFiles(doneResults.map((r) => ({ name: r.name, blob: r.blob })));
        downloadBlob(zip, 'no-background-images.zip');
    };

    const status = deriveToolStatus({
        hasInput: files.length > 0,
        canRun,
        processing,
        hasResult: doneResults.length > 0,
        hasError: Boolean(error),
    });

    // ── Before/after lightbox ──
    // Object URLs live as long as the results do; the modal only ever gets URLs,
    // never blobs, so they are made once here instead of per render.
    const [lightboxIndex, setLightboxIndex] = useState(null);
    const [compareUrls, setCompareUrls] = useState([]);

    useEffect(() => {
        if (!doneResults.length) {
            setCompareUrls([]);
            return undefined;
        }
        let cancelled = false;
        const made = results.map((r) => ({
            after: r ? URL.createObjectURL(r.blob) : null,
            before: null,
        }));
        setCompareUrls(made);
        // The "before" pane draws the uploaded file itself, so a HEIC has to be
        // decoded first — a raw .heic paints an empty white pane everywhere but
        // Safari. Everything else resolves immediately.
        (async () => {
            for (let i = 0; i < results.length; i += 1) {
                if (!results[i] || !files[i]) continue;
                const blob = await toDisplayableBlob(files[i]); // eslint-disable-line no-await-in-loop
                if (cancelled) return;
                const url = URL.createObjectURL(blob);
                made[i].before = url;
                setCompareUrls((prev) => {
                    const next = [...prev];
                    next[i] = { ...next[i], before: url };
                    return next;
                });
            }
        })();
        return () => {
            cancelled = true;
            made.forEach(({ after, before }) => {
                if (after) URL.revokeObjectURL(after);
                if (before) URL.revokeObjectURL(before);
            });
        };
    }, [results, doneResults.length, files]);

    const compareItems = useMemo(
        () => results
            .map((r, i) => (r ? {
                id: i,
                fileName: r.name,
                mimeType: r.blob.type || 'image/png',
                aiImage: compareUrls[i]?.after,
                // What the shared modal calls the "before" pane.
                uploadedImage: compareUrls[i]?.before,
            } : null))
            .filter((item) => item?.aiImage),
        [results, compareUrls],
    );

    const openLightbox = (index) => {
        if (!results[index]) return;
        setPreviewIndex(index);
        setLightboxIndex(index);
    };

    const successLabel = doneResults.length > 1
        ? `${t('removeBackground.resultsTitle')} (${toBnDigits(doneResults.length)})`
        : t('removeBackground.done');

    // With several files the count is the useful line; the one-time model
    // download is the only thing worth interrupting it for.
    const processingLabel = progress?.pct > 0 && progress.pct < 100
        ? t('removeBackground.progress', { percent: toBnDigits(progress.pct) })
        : progress?.fileTotal > 1
            ? t('removeBackground.progressFile', {
                file: toBnDigits(progress.fileIndex),
                files: toBnDigits(progress.fileTotal),
            })
            : t('removeBackground.removing');

    // The bar tracks the whole batch, not the file in hand: files already done
    // plus how far the current one has got.
    const overallProgress = useMemo(() => {
        if (!progress?.fileTotal) return undefined;
        const done = Math.max(0, progress.fileIndex - 1) + Math.min(1, (progress.pct || 0) / 100);
        return Math.max(5, Math.round((done / progress.fileTotal) * 100));
    }, [progress]);

    const statusText = getToolStatusText(status, t, {
        ready: t('removeBackground.readyHint'),
        success: t('removeBackground.done'),
    });

    // A colour the presets do not carry means the picker tile is the active one.
    const isCustomColor = !bgPattern
        && !BACKGROUND_COLOR_SWATCHES.some((swatch) => swatch.value.toLowerCase() === bgColor.toLowerCase());
    // Its icon has to stay readable on whatever was picked.
    const customColorIsLight = useMemo(() => {
        const hex = bgColor.replace('#', '');
        if (hex.length !== 6) return true;
        const r = parseInt(hex.slice(0, 2), 16);
        const g = parseInt(hex.slice(2, 4), 16);
        const b = parseInt(hex.slice(4, 6), 16);
        return (0.299 * r + 0.587 * g + 0.114 * b) / 255 > 0.55;
    }, [bgColor]);

    // The settings panel stretches to the preview's height only when that is worth
    // it: one or two pictures (the preview then sizes them to its full height) and a
    // mode with a scroller to fill — রঙ or ছবি. Everywhere else it keeps its own size.
    const stretchSettings = (bgMode === 'color' || bgMode === 'image')
        && files.length >= 1 && files.length <= 2;
    const stretchCol = stretchSettings ? 'lg:flex lg:min-h-0 lg:flex-1 lg:flex-col' : '';

    const updateBgMode = (mode) => {
        setBgMode(mode);
        if (mode !== 'image') {
            setBgImageFile(null);
            setBgPhotoId(null);
        } else if (!bgImageFile && !bgPhotoId) {
            setBgPhotoId(DEFAULT_PHOTO_PRESET_ID);
        }
    };

    return (
        <>
        <ToolPageLayout
            slug="image/remove-background"
            subtitleKey="removeBackground.subtitle"
            status={status}
            statusText={allDone && !processing ? successLabel : statusText}
            onReset={files.length || error ? handleReset : undefined}
            // Before a run the bar runs the tool; once every picture is done it downloads.
            primaryAction={allDone && !processing ? {
                label: doneResults.length > 1 ? t('removeBackground.downloadZip') : t('removeBackground.download'),
                onClick: handleDownload,
                icon: IconDownload,
            } : {
                label: t('removeBackground.remove'),
                onClick: handleRemoveBackground,
                disabled: !canRun || processing,
                loading: processing,
            }}
            // Drops the results only — the pictures and settings stay loaded, so another
            // run is one click away. Clearing everything is what রিসেট is for.
            secondaryAction={allDone && !processing ? {
                label: t('removeBackground.startOver'),
                onClick: invalidateResults,
                icon: IconRefresh,
            } : undefined}
            showTrust={false}
            settings={(
                // One white panel for the whole column: tabs, the booth, the new
                // background and the output format are sections of it, divided by
                // hairlines, instead of a stack of separate cards.
                //
                // With one or two pictures in রঙ / ছবি mode (see stretchSettings) the
                // panel stretches to the preview's height on desktop and the colour /
                // photo scroller grows into whatever is left, so both columns end on
                // the same line.
                <div className={`rounded-[20px] border border-[#ECE8E4] bg-white p-4 shadow-[0_1px_2px_rgba(28,24,21,0.04)] dark:border-zinc-700 dark:bg-zinc-900 ${stretchCol}`}>
                    {/* Files are picked in the preview panel, so the left column
                        carries settings only — this input is what both the empty
                        state and the grid's "+" tile open. */}
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept={ACCEPT}
                        multiple
                        className="hidden"
                        onChange={(e) => { addFiles(e.target.files); e.target.value = ''; }}
                    />

                    {/* সেটিংস / ফটো বুথ tabs — booth tab only while the booth is ON. The
                        সিলেক্ট button adds every chosen photo to the batch and returns here.
                        The booth pane stays mounted (hidden) so the on/off status resolves. */}
                    <div>
                        {boothOn && (
                            <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
                                {[
                                    { id: 'settings', label: t('removeBackground.settingsTitle') },
                                    { id: 'booth', label: t('photoCollectorPanel.title') },
                                ].map((tb) => (
                                    <button key={tb.id} type="button" onClick={() => setSideTab(tb.id)}
                                        className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${sideTab === tb.id ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm' : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'}`}>
                                        {tb.label}
                                    </button>
                                ))}
                            </div>
                        )}
                        <div className={boothOn && sideTab === 'booth' ? 'mt-3' : 'hidden'}>
                            <PhotoBoothInbox
                                multiSelect
                                markedIds={addedBoothIds}
                                onPickFiles={(picked, ids) => {
                                    picked.forEach((f, i) => boothFileIds.current.set(f, ids[i]));
                                    addFiles(picked);
                                    setSideTab('settings');
                                }}
                                onStatusChange={setBoothOn}
                            />
                        </div>
                    </div>
                    <div className={boothOn && sideTab === 'booth' ? 'hidden' : `space-y-4 ${boothOn ? 'mt-4' : ''} ${stretchCol}`}>
                    <section className={stretchCol}>
                        <p className="mb-2.5 text-[13px] font-semibold text-[#3D3833] dark:text-zinc-200">
                            {t('removeBackground.bgTitle')}
                        </p>
                        <div className={`space-y-2.5 ${stretchCol}`}>
                            <div className="flex gap-1 rounded-xl bg-[#F7F5F3] p-1 dark:bg-zinc-800">
                                {/* Icons only — the name is in the tooltip and
                                    read out via aria-label. */}
                                {BG_MODE_OPTIONS.map((opt) => {
                                    const Icon = BG_MODE_ICONS[opt.id];
                                    const label = t(opt.labelKey);
                                    return (
                                        <Hint label={label} key={opt.id}>
                                            <button
                                                type="button"
                                                // Never locked during a run: the background is painted on
                                                // top of each finished cut-out, so changing it mid-batch
                                                // just re-paints what is done and applies to the rest.
                                                onClick={() => updateBgMode(opt.id)}
                                                className={segBtnClass(bgMode === opt.id)}
                                                aria-label={label}
                                                aria-pressed={bgMode === opt.id}
                                            >
                                                <Icon size={20} stroke={1.8} />
                                            </button>
                                        </Hint>
                                    );
                                })}
                            </div>

                            {bgMode === 'color' ? (
                                // Scrolls inside a fixed height: 19 solids + 15 patterns laid
                                // out in full made the settings column — and the preview panel
                                // that stretches to match it — far taller than the work needs.
                                // The padding keeps the scaled-up active tile from being clipped.
                                <div className={`-mx-1 h-[340px] space-y-2.5 overflow-y-auto overscroll-contain p-1 pt-0.5 [scrollbar-width:thin] ${
                                    stretchSettings ? 'lg:h-auto lg:min-h-[240px] lg:flex-1 lg:basis-0' : ''
                                }`}>
                                    <p className="text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                        {t('removeBackground.colorLabel')}
                                    </p>
                                    {/* The site's palette: nineteen solids plus the free
                                        picker fill four even rows of five. The picker tile
                                        is tinted with whatever colour it is holding. */}
                                    <div className="grid grid-cols-5 gap-2">
                                        {BACKGROUND_COLOR_SWATCHES.map((swatch) => {
                                            const active = !bgPattern && bgColor.toLowerCase() === swatch.value.toLowerCase();
                                            return (
                                                <Hint label={swatch.label} key={swatch.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setBgColor(swatch.value); setBgPatternId(null); }}
                                                        aria-label={swatch.label}
                                                        aria-pressed={active}
                                                        className={`aspect-square w-full rounded-lg border-2 transition-transform ${
                                                            active
                                                                ? 'scale-105 border-amber-500'
                                                                : 'border-[#ECE8E4] hover:scale-105 dark:border-zinc-600'
                                                        }`}
                                                        style={{ backgroundColor: swatch.value }}
                                                    />
                                                </Hint>
                                            );
                                        })}
                                        <Hint label={t('removeBackground.customColor')}>
                                            <label
                                                aria-label={t('removeBackground.customColor')}
                                                className={`relative flex aspect-square w-full cursor-pointer items-center justify-center rounded-lg border-2 transition-transform ${
                                                    isCustomColor
                                                        ? 'scale-105 border-amber-500'
                                                        : 'border-[#ECE8E4] hover:scale-105 dark:border-zinc-600'
                                                }`}
                                                style={isCustomColor ? { backgroundColor: bgColor } : undefined}
                                            >
                                                <IconColorPicker
                                                    size={15}
                                                    stroke={2}
                                                    className={isCustomColor && !customColorIsLight
                                                        ? 'text-white'
                                                        : 'text-[#6B635C] dark:text-zinc-300'}
                                                />
                                                <input
                                                    type="color"
                                                    value={bgColor}
                                                    onChange={(e) => { setBgColor(e.target.value); setBgPatternId(null); }}
                                                    className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                                                />
                                            </label>
                                        </Hint>
                                    </div>

                                    <p className="pt-1 text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                        {t('removeBackground.patternLabel')}
                                    </p>
                                    <div className="grid grid-cols-5 gap-2">
                                        {BACKGROUND_COLOR_PATTERNS.map((preset) => {
                                            const active = bgPatternId === preset.id;
                                            return (
                                                <Hint label={preset.label} key={preset.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setBgPatternId(preset.id)}
                                                        aria-label={preset.label}
                                                        aria-pressed={active}
                                                        className={`aspect-square w-full rounded-lg border-2 transition-transform ${
                                                            active
                                                                ? 'scale-105 border-amber-500'
                                                                : 'border-[#ECE8E4] hover:scale-105 dark:border-zinc-600'
                                                        }`}
                                                        style={{ background: patternCss(preset) }}
                                                    />
                                                </Hint>
                                            );
                                        })}
                                    </div>
                                </div>
                            ) : null}

                            {bgMode === 'image' ? (
                                <div className={`space-y-2 pt-0.5 ${stretchCol}`}>
                                    <input
                                        ref={bgImageInputRef}
                                        type="file"
                                        accept={ACCEPT}
                                        className="hidden"
                                        onChange={(e) => {
                                            const picked = Array.from(e.target.files || [])[0];
                                            if (picked && isSupportedRemoveBgFile(picked)) {
                                                setBgImageFile(picked);
                                                setBgPhotoId(null);
                                            }
                                            e.target.value = '';
                                        }}
                                    />
                                    {/* One grid for both sources: the first tile uploads your own
                                        background (and shows it once picked), the rest are the
                                        site's hosted presets — one tap, no upload. */}
                                    <p className="text-[12px] font-semibold text-[#6B635C] dark:text-zinc-400">
                                        {t('removeBackground.photoPresetLabel')}
                                    </p>
                                    {/* Same reason as the colour grid: a fixed-height scroller.
                                        The height and the scrolling live on a WRAPPER, never on
                                        the grid itself — a grid with a fixed height squeezed its
                                        4:3 tiles' rows to fit, and the photos piled on top of
                                        each other. */}
                                    <div className={`-mx-1 h-[316px] overflow-y-auto overscroll-contain p-1 [scrollbar-width:thin] ${
                                        stretchSettings ? 'lg:h-auto lg:min-h-[220px] lg:flex-1 lg:basis-0' : ''
                                    }`}>
                                    <div className="grid grid-cols-3 gap-2">
                                        <div
                                            className={`group relative aspect-[4/3] w-full overflow-hidden rounded-lg border-2 transition-transform ${
                                                bgImageFile
                                                    ? 'scale-[1.03] border-amber-500'
                                                    : 'border-dashed border-[#DCD6D0] bg-[#FBFAF9] hover:scale-[1.03] hover:border-amber-400 dark:border-zinc-600 dark:bg-zinc-900'
                                            }`}
                                        >
                                            <Hint label={bgImageFile ? t('removeBackground.changeBgImage') : t('removeBackground.uploadBgImage')}>
                                                <button
                                                    type="button"
                                                    onClick={() => bgImageInputRef.current?.click()}
                                                    aria-label={bgImageFile ? t('removeBackground.changeBgImage') : t('removeBackground.uploadBgImage')}
                                                    className="flex h-full w-full items-center justify-center"
                                                >
                                                    {bgImageFile && bgImageUrl ? (
                                                        <>
                                                            <img src={bgImageUrl} alt="" draggable={false} className="h-full w-full object-cover" />
                                                            <span className="absolute inset-0 flex items-center justify-center bg-black/45 text-white opacity-0 transition-opacity group-hover:opacity-100">
                                                                <IconRefresh size={16} stroke={2} />
                                                            </span>
                                                        </>
                                                    ) : (
                                                        <IconPlus size={22} stroke={2} className="text-amber-500" />
                                                    )}
                                                </button>
                                            </Hint>
                                            {bgImageFile ? (
                                                <Hint label={t('removeBackground.removeBgImage')}>
                                                    <button
                                                        type="button"
                                                        onClick={() => setBgImageFile(null)}
                                                        aria-label={t('removeBackground.removeBgImage')}
                                                        className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-md bg-white/95 text-red-500 shadow-sm transition-opacity hover:bg-red-50 md:opacity-0 md:group-hover:opacity-100"
                                                    >
                                                        <IconX size={12} stroke={2.4} />
                                                    </button>
                                                </Hint>
                                            ) : null}
                                        </div>

                                        {BACKGROUND_PHOTO_PRESETS.map((preset) => {
                                            const active = !bgImageFile && bgPhotoId === preset.id;
                                            return (
                                                <Hint label={preset.label} key={preset.id}>
                                                    <button
                                                        type="button"
                                                        onClick={() => { setBgPhotoId(preset.id); setBgImageFile(null); }}
                                                        aria-label={preset.label}
                                                        aria-pressed={active}
                                                        className={`group relative aspect-[4/3] w-full overflow-hidden rounded-lg border-2 bg-[#F7F5F3] transition-transform dark:bg-zinc-800 ${
                                                            active
                                                                ? 'scale-[1.03] border-amber-500'
                                                                : 'border-[#ECE8E4] hover:scale-[1.03] dark:border-zinc-600'
                                                        }`}
                                                    >
                                                        <img
                                                            src={preset.thumb}
                                                            alt=""
                                                            loading="lazy"
                                                            draggable={false}
                                                            className="h-full w-full object-cover"
                                                        />
                                                        <span className="absolute inset-x-0 bottom-0 truncate bg-gradient-to-t from-black/60 to-transparent px-1.5 pb-1 pt-3 text-left text-[10px] font-semibold text-white">
                                                            {preset.label}
                                                        </span>
                                                    </button>
                                                </Hint>
                                            );
                                        })}
                                    </div>
                                    </div>
                                </div>
                            ) : null}
                        </div>
                    </section>

                    <div className="border-t border-[#F0EEEB] pt-4 dark:border-zinc-800">
                        <OutputFormatPicker
                            bare
                            value={format}
                            onChange={setFormat}
                            formats={['original', 'jpg', 'png', 'webp', 'avif']}
                            transparentOutput={bgMode === 'transparent'}
                        />
                    </div>
                    </div>
                </div>
            )}
            preview={(
                <ToolPreviewPanel
                    title={t('removeBackground.previewTitle')}
                    status={status}
                    statusLabel={t(`toolPage.status.${status}`)}
                    idleTitle={t('removeBackground.previewEmpty')}
                    idleHint={t('removeBackground.previewEmptyHint')}
                    processingLabel={processingLabel}
                    progress={overallProgress}
                    error={error}
                    canvasTone="plain"
                    processingTone="none"
                    // Grow with the settings column: the drop zone / grid fills the
                    // panel down to its bottom edge instead of leaving a white gap
                    // under it — but never shorter than a comfortable working area.
                    // On desktop the floor (700px) is taller than the settings column
                    // in any mode, so switching স্বচ্ছ / রঙ / ছবি never makes the
                    // preview grow or shrink, and one or two pictures get real room.
                    fill
                    fillMinHeightClass="min-h-[min(560px,70vh)] lg:min-h-[700px]"
                    showPasteHint
                    showIdle={!files.length && !error}
                    onIdleClick={() => fileInputRef.current?.click()}
                    onIdleDrop={handleDrop}
                    onIdleDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                    onIdleDragLeave={() => setIsDragging(false)}
                    idleDragging={isDragging}
                >
                    {/* Kept mounted while processing: each card spins on its own, so
                        the wait shows the work and finished cut-outs can be opened. */}
                    {files.length > 0 && !error ? (
                        <ImageGridPreview
                            // Crop / rotate / copy / delete stay on every card, during
                            // the run as well: the grid hides them on the cards that are
                            // still working, so a picture that is already done can be
                            // edited without waiting for the rest of the batch. The
                            // buttons stop the click, so the card itself still opens the
                            // before/after compare.
                            actions={fileActions}
                            files={files}
                            results={results}
                            variant="alpha"
                            adaptive
                            checkerboard={bgMode === 'transparent'}
                            busyIndexes={processing ? unresolvedIndexes : undefined}
                            failedIndexes={failedIndexes}
                            failedLabel={t('removeBackground.errors.cardFailed')}
                            onDownload={(i) => {
                                const r = results[i];
                                if (r) downloadBlob(r.blob, r.name);
                            }}
                            downloadLabel={t('common.download')}
                            activeIndex={doneResults.length ? previewIndex : undefined}
                            onItemClick={doneResults.length ? openLightbox : undefined}
                            onAddMore={processing ? undefined : () => fileInputRef.current?.click()}
                            onAddDrop={processing ? undefined : handleDrop}
                            addLabel={t('removeBackground.addMore')}
                        />
                    ) : null}
                </ToolPreviewPanel>
            )}
        />
        {cropDialog}
        {/* Full-screen wipe compare: the original on the left of the handle, the
            result on the right, with zoom / pan / adjustments and download. */}
        <ImageCompareSliderModal
            opened={lightboxIndex !== null}
            onClose={() => setLightboxIndex(null)}
            images={compareItems}
            initialImage={compareItems.find((item) => item.id === lightboxIndex) ?? null}
            // `adjusted` is the result re-rendered with the viewer's
            // brightness/contrast/sharpness edits burnt in; without edits it is
            // null and the untouched result goes out.
            onDownloadImage={(img, adjusted) => {
                const r = results[img?.id];
                if (!r) return;
                downloadBlob(adjusted || r.blob, r.name);
            }}
            useImageTitle
            checkerboard={bgMode === 'transparent'}
        />
        </>
    );
}
